/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { diff as deepDiff } from 'deep-diff';
import { isObject, union } from 'underscore';
import { Pipe, PipeTransform } from '@angular/core';

import {
    DiffTypeItem,
    IAviDiffGridItem,
    IAviDiffGridRow,
    IAviDiffGridSourcePair,
    TDiffType,
} from './avi-diff-grid.types';

/**
 * @description
 *     Pipe for transforming an AviDiffGridSourcePair to diff grid rows.
 *     An instance of AviDiffGridSourcePair contains two objects to be used as diff source, and
 *     it usually needs another layer of processing to be built, which is done by a custom diff
 *     source pair pipe.
 * @author Zhiqian Liu, Deepak Chaudhary
 */
@Pipe({
    name: 'aviDiffGridRows',
})
export class AviDiffGridRowsPipe implements PipeTransform {
    /**
     * Suffix appended to each value in the hashes to help perform diffs on values with duplicated
     * keys.
     * To be overwritten when this.tranform is called.
     */
    public diffPostfix = '';

    /**
     * Transform function for the pipe that takes a diff source pair, compares the two source
     * objects inside it, and produces a list diff grid rows that map to properties from the objects
     * side by side with difference information.
     */
    public transform(
        diffSourcePair: IAviDiffGridSourcePair,
    ): IAviDiffGridRow[] {
        const {
            leftSource,
            rightSource,
            diffPostfix,
            performNoDiff,
        } = diffSourcePair;

        this.diffPostfix = diffPostfix || '';

        const rows: IAviDiffGridRow[] = [];
        const diffs = deepDiff(leftSource, rightSource) ?? [];
        let formattedDiff: Record<string, unknown> = {};

        for (const diffItem of diffs) {
            formattedDiff = this.parseDiffItem(diffItem, formattedDiff);
        }

        this.generateDiffRows(leftSource, rightSource, formattedDiff, rows, performNoDiff);

        return rows;
    }

    private generateDiffRows(
        leftSource: Record<string, unknown>,
        rightSource: Record<string, unknown>,
        formattedDiff: Record<string, unknown>,
        rows: IAviDiffGridRow[],
        performNoDiff: boolean,
        parentType: TDiffType = DiffTypeItem.NO_DIFF,
        depth = 0,
    ): void {
        const leftSourceKeys = Object.keys(leftSource ?? {});
        const rightSourceKeys = Object.keys(rightSource ?? {});
        const keys = union(leftSourceKeys, rightSourceKeys).sort();

        keys.forEach(key => {
            const lhsValue = leftSource?.[key];
            const rhsValue = rightSource?.[key];

            if (lhsValue !== undefined && !isObject(lhsValue) || rhsValue && !isObject(rhsValue)) {
                const keyWithoutDiffPostfix = this.removeDiffPostfix(key);
                let lhsContent = '';
                let rhsContent = '';
                let type: TDiffType = DiffTypeItem.NO_DIFF;

                if (formattedDiff && formattedDiff[key]) {
                    const { kind } = formattedDiff[key] as IAviDiffGridItem;

                    type = this.getRowType(kind);
                }

                switch (type) {
                    case DiffTypeItem.DELETED:
                        lhsContent = `${keyWithoutDiffPostfix}: ${lhsValue}`;
                        break;
                    case DiffTypeItem.NEW:
                        rhsContent = `${keyWithoutDiffPostfix}: ${rhsValue}`;
                        break;
                    case DiffTypeItem.EDIT:
                    case DiffTypeItem.NO_DIFF:
                        lhsContent = `${keyWithoutDiffPostfix}: ${lhsValue}`;
                        rhsContent = `${keyWithoutDiffPostfix}: ${rhsValue}`;
                        break;
                }

                switch (parentType) {
                    case DiffTypeItem.DELETED:
                        type = DiffTypeItem.DELETED;
                        rhsContent = '';
                        break;
                    case DiffTypeItem.NEW:
                        type = DiffTypeItem.NEW;
                        lhsContent = '';
                        break;
                }

                rows.push({
                    left: lhsContent,
                    right: rhsContent,
                    type: performNoDiff ? DiffTypeItem.NO_DIFF : type,
                    depth,
                });
            } else {
                const keyWithoutDiffPostfix = this.removeDiffPostfix(key);
                let lhsContent = '';
                let rhsContent = '';
                let type: TDiffType = parentType;

                if (formattedDiff && formattedDiff[key]) {
                    const { kind } = formattedDiff[key] as IAviDiffGridItem;

                    type = this.getRowType(kind);
                }

                switch (type) {
                    case DiffTypeItem.DELETED:
                        lhsContent = keyWithoutDiffPostfix;
                        break;
                    case DiffTypeItem.NEW:
                        rhsContent = keyWithoutDiffPostfix;
                        break;
                    case DiffTypeItem.EDIT:
                    case DiffTypeItem.NO_DIFF:
                        lhsContent = keyWithoutDiffPostfix;
                        rhsContent = keyWithoutDiffPostfix;
                        break;
                }

                rows.push({
                    left: lhsContent,
                    right: rhsContent,
                    type: performNoDiff ? DiffTypeItem.NO_DIFF : type,
                    depth,
                });

                return this.generateDiffRows(
                    lhsValue as Record<string, string>,
                    rhsValue as Record<string, string>,
                    formattedDiff?.[key] as Record<string, unknown>,
                    rows,
                    performNoDiff,
                    type,
                    depth + 1,
                );
            }
        });
    }

    /**
     * Return the row type based on the given kind.
     */
    private getRowType(kind: string): TDiffType {
        const typeMap: Record<string, TDiffType> = {
            E: DiffTypeItem.EDIT,
            N: DiffTypeItem.NEW,
            D: DiffTypeItem.DELETED,
        };

        return typeMap[kind] || DiffTypeItem.NO_DIFF;
    }

    /**
     * Process a single diff item and attach it to the given parent object.
     * This function is recursive and will create a nested object if the diff item's path is not empty.
     */
    private parseDiffItem(diffItem: IAviDiffGridItem, parent: Record<string, unknown>): Record<string, unknown> {
        // if the diff item does not have a path, we can directly update the parent.
        if (!diffItem.path || diffItem.path.length === 0) {
            return {
                kind: diffItem.kind,
                right: diffItem.rhs,
                left: diffItem.lhs,
            };
        }

        // if the diff item has a path, we need to traverse the parent object to find the correct location to update.
        const updatedParent = parent;
        const path = diffItem.kind === 'A' ? diffItem.path : diffItem.path.slice(0, -1);

        // traverse the path to find the correct location
        for (const key of path) {
            parent = parent[key] as Record<string, unknown> || (parent[key] = {});
        }

        // if the diff item is an array item, we need to update the correct index.
        if (diffItem.kind === 'A') {
            const { index, item } = diffItem;

            parent[index] = this.parseDiffItem(
                item as IAviDiffGridItem,
                parent[index] as Record<string, unknown> || {},
            );
        } else {
            // otherwise, we update the parent with the diff item's properties.
            parent[diffItem.path[diffItem.path.length - 1]] = {
                kind: diffItem.kind,
                right: diffItem.rhs,
                left: diffItem.lhs,
            };
        }

        return updatedParent;
    }

    /**
     * Append a postfix to duplicate field keys in order to perform diff checking.
     * Since we are using comparison feature mostly for objects, we do not have properties with the
     * same names within one object. But this is not the case under some situations
     * (ex. HTTP headers, and they usually comes as a concatenated string that makes key duplication
     * possible.) To make it work a postfix is added to the duplicated names for the sake of data
     * processing, to hide it from user on layout rendering.
     */
    private removeDiffPostfix(name: string): string {
        const pos = name.lastIndexOf(this.diffPostfix);

        if (pos !== -1) {
            name = name.slice(0, pos);
        }

        return name;
    }
}
